import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiMonitor, FiVolume2, FiSliders,
  FiDatabase, FiInfo, FiDownload, FiTrash2,
  FiActivity, FiMessageSquare, FiGlobe, FiCheck,
  FiMail, FiCalendar, FiShield, FiEdit2, FiKey, FiLogOut,
} from 'react-icons/fi';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { useTranslation } from '../i18n/useTranslation';
import { getAllAuditLogs } from '../services/auditService';
import { logOut, updateUserProfile } from '../services/authService';
import { getSessionLogs, getUserCustomDecks } from '../services/firestoreService';
import { getProfiles } from '../services/profileService';
import { CharCounter } from '../components/forms/CharCounter';
import { sanitizeInput, CHAR_LIMITS } from '../utils/sanitize';
import { FeedbackForm } from '../components/FeedbackModal';
import { loadSettings, applySettings } from '../hooks/useApplySettings';
import toast from 'react-hot-toast';
import '../styles/settings.css';
import '../styles/feedback.css';
import '../styles/account.css';

function getInitials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map((w) => w[0].toUpperCase()).slice(0, 2).join('');
}

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

const SETTINGS_KEY = 'sc-settings';
const DEFAULTS = {
  theme: 'light', fontSize: 'medium', animations: true,
  soundEffects: true, voicePrompts: false, autoSave: true,
};

function saveSettings(patch) {
  const current = loadSettings();
  const next = { ...current, ...patch };
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
  applySettings(next);
  return next;
}

// ── Small reusable pieces ────────────────────────────────────────────────────

const Toggle = ({ checked, onChange }) => (
  <label className="sc-toggle">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
    <span className="sc-toggle-slider" />
  </label>
);

const SettingRow = ({ label, sublabel, children, last }) => (
  <div className={`st-row${last ? ' st-row--last' : ''}`}>
    <div className="st-row-text">
      <span className="st-row-label">{label}</span>
      {sublabel && <span className="st-row-sub">{sublabel}</span>}
    </div>
    <div className="st-row-control">{children}</div>
  </div>
);

const Section = ({ icon, title, children }) => (
  <div className="st-section">
    <div className="st-section-header">
      <span className="st-section-icon">{icon}</span>
      <h3 className="st-section-title">{title}</h3>
    </div>
    <div className="st-section-body">{children}</div>
  </div>
);

// Pill selector for theme / font size
const PillGroup = ({ options, value, onChange }) => (
  <div className="st-pill-group">
    {options.map(opt => (
      <button
        key={opt.value}
        type="button"
        className={`st-pill${value === opt.value ? ' st-pill--active' : ''}`}
        onClick={() => onChange(opt.value)}
      >
        {opt.icon && <span className="st-pill-icon">{opt.icon}</span>}
        {opt.label}
      </button>
    ))}
  </div>
);

// ── Main page ────────────────────────────────────────────────────────────────

export const SettingsPage = () => {
  const { currentUser, userData } = useAuth();
  const isAdmin = userData?.role === 'admin';
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const saved = loadSettings();
  const [fontSize,     setFontSize]     = useState(saved.fontSize);
  const [animations,   setAnimations]   = useState(saved.animations !== false);
  const [soundEffects, setSoundEffects] = useState(saved.soundEffects !== false);
  const [voicePrompts, setVoicePrompts] = useState(!!saved.voicePrompts);
  const [autoSave,     setAutoSave]     = useState(saved.autoSave !== false);

  const [auditLogs,    setAuditLogs]    = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditVisible, setAuditVisible] = useState(false);
  const [cleared,      setCleared]      = useState(false);

  // ── Profile (merged from the old My Account page) ─────────────────────────
  const displayName = currentUser?.displayName || userData?.displayName || 'Therapist';
  const email = currentUser?.email || '';
  const memberSince = formatDate(currentUser?.metadata?.creationTime);

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    fullName: displayName,
    email: email,
    jobTitle: userData?.jobTitle || 'ABA Therapist',
    organization: userData?.clinic || 'My Clinic',
  });

  const [profileStats, setProfileStats] = useState({ decks: 0, sessions: 0, profiles: 0, avgAccuracy: null });

  useEffect(() => {
    if (!currentUser?.uid) return;
    const uid = currentUser.uid;
    Promise.all([
      getUserCustomDecks(uid),
      getSessionLogs(uid),
      getProfiles(uid),
    ]).then(([decksRes, sessionsRes, profilesRes]) => {
      const decks    = decksRes.success    ? decksRes.data.length    : 0;
      const sessions = sessionsRes.success ? sessionsRes.data.length : 0;
      const profiles = profilesRes.success ? profilesRes.data.length : 0;
      let avgAccuracy = null;
      if (sessionsRes.success && sessionsRes.data.length > 0) {
        const accuracies = sessionsRes.data.map(s => s.accuracy).filter(a => typeof a === 'number');
        if (accuracies.length > 0) {
          avgAccuracy = Math.round(accuracies.reduce((a, b) => a + b, 0) / accuracies.length);
        }
      }
      setProfileStats({ decks, sessions, profiles, avgAccuracy });
    });
  }, [currentUser]);

  const handleProfileChange = (field, limitKey) => (e) => {
    const raw = e.target.value;
    const max = CHAR_LIMITS[limitKey];
    if (max && raw.length > max) return;
    setProfileForm((f) => ({ ...f, [field]: raw }));
  };

  const handleProfileSave = async () => {
    const cleaned = {
      fullName:     sanitizeInput(profileForm.fullName),
      email:        profileForm.email.trim(),
      jobTitle:     sanitizeInput(profileForm.jobTitle),
      organization: sanitizeInput(profileForm.organization),
    };
    setProfileForm(cleaned);
    try {
      if (currentUser?.uid) {
        await updateUserProfile(currentUser.uid, {
          displayName: cleaned.fullName,
          jobTitle: cleaned.jobTitle,
          clinic: cleaned.organization,
        });
      }
      toast.success('Profile updated!');
    } catch {
      toast.error('Could not save profile — please try again.');
    }
    setIsEditingProfile(false);
  };

  const handleLogOut = async () => {
    await logOut();
    navigate('/login');
  };

  // Apply any change immediately
  useEffect(() => {
    saveSettings({ theme: 'light', fontSize, animations, soundEffects, voicePrompts, autoSave });
  }, [fontSize, animations, soundEffects, voicePrompts, autoSave]);

  const loadAuditLogs = async () => {
    if (auditLogs.length > 0) { setAuditVisible(true); return; }
    setAuditLoading(true);
    const logs = await getAllAuditLogs();
    setAuditLogs(logs);
    setAuditLoading(false);
    setAuditVisible(true);
  };

  const handleClearData = () => {
    if (!window.confirm('Clear all local settings and cached data? This cannot be undone.')) return;
    const keys = Object.keys(localStorage).filter(k => k.startsWith('sc-'));
    keys.forEach(k => localStorage.removeItem(k));
    setCleared(true);
    setTimeout(() => setCleared(false), 2500);
  };

  return (
    <Layout>
      <div className="settings-page">
        <div className="st-page-header">
          <h1 className="st-page-title">{t('settings.title')}</h1>
          <p className="st-page-sub">{t('settings.subtitle')}</p>
        </div>

        {/* ── Profile (merged from the old My Account page) ─────────────── */}
        <div className="account-grid">
          <div className="account-main">
            <div className="ac-card">
              <div className="ac-card-header">
                <h2 className="ac-card-title">Profile Information</h2>
                <button
                  className={`ac-edit-btn ${isEditingProfile ? 'saving' : ''}`}
                  onClick={isEditingProfile ? handleProfileSave : () => setIsEditingProfile(true)}
                >
                  {isEditingProfile ? 'Save Changes' : <><FiEdit2 size={14} /> Edit Profile</>}
                </button>
              </div>

              <div className="ac-fields">
                <div className="ac-field-row">
                  <div className="ac-field">
                    <label className="ac-field-label">Full Name</label>
                    <input
                      className="ac-field-input"
                      value={profileForm.fullName}
                      onChange={handleProfileChange('fullName', 'profileName')}
                      readOnly={!isEditingProfile}
                      maxLength={CHAR_LIMITS.profileName}
                    />
                    {isEditingProfile && <CharCounter current={profileForm.fullName.length} max={CHAR_LIMITS.profileName} />}
                  </div>
                  <div className="ac-field">
                    <label className="ac-field-label">Email Address</label>
                    <input
                      className="ac-field-input"
                      value={profileForm.email}
                      readOnly
                      type="email"
                      title="To change your email, contact hello@stimulicentral.org"
                    />
                    {isEditingProfile && <span style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4, display: 'block' }}>Email cannot be changed here. Contact support.</span>}
                  </div>
                </div>
                <div className="ac-field-row">
                  <div className="ac-field">
                    <label className="ac-field-label">Professional Role</label>
                    <input
                      className="ac-field-input"
                      value={profileForm.jobTitle}
                      onChange={handleProfileChange('jobTitle', 'jobTitle')}
                      readOnly={!isEditingProfile}
                      maxLength={CHAR_LIMITS.jobTitle}
                    />
                    {isEditingProfile && <CharCounter current={profileForm.jobTitle.length} max={CHAR_LIMITS.jobTitle} />}
                  </div>
                  <div className="ac-field">
                    <label className="ac-field-label">Organization</label>
                    <input
                      className="ac-field-input"
                      value={profileForm.organization}
                      onChange={handleProfileChange('organization', 'organization')}
                      readOnly={!isEditingProfile}
                      maxLength={CHAR_LIMITS.organization}
                    />
                    {isEditingProfile && <CharCounter current={profileForm.organization.length} max={CHAR_LIMITS.organization} />}
                  </div>
                </div>
              </div>
            </div>

            <div className="ac-card">
              <div className="ac-card-title" style={{ marginBottom: 16 }}>Account Statistics</div>
              <hr style={{ height: 1, background: '#E5E7EB', border: 'none', marginBottom: 16 }} />
              <div className="ac-stats-grid">
                <div className="ac-stat-box">
                  <span className="ac-stat-val">{profileStats.decks}</span>
                  <span className="ac-stat-name">Flashcard Sets</span>
                </div>
                <div className="ac-stat-box">
                  <span className="ac-stat-val">{profileStats.sessions}</span>
                  <span className="ac-stat-name">Sessions Completed</span>
                </div>
                <div className="ac-stat-box">
                  <span className="ac-stat-val">{profileStats.profiles}</span>
                  <span className="ac-stat-name">Active Profiles</span>
                </div>
                <div className="ac-stat-box accent">
                  <span className="ac-stat-val accent-val">{profileStats.avgAccuracy !== null ? `${profileStats.avgAccuracy}%` : '—'}</span>
                  <span className="ac-stat-name">Avg Progress %</span>
                </div>
              </div>
            </div>
          </div>

          <div className="account-sidebar">
            <div className="ac-card ac-profile-card">
              <div className="ac-avatar">{getInitials(profileForm.fullName)}</div>
              <h3 className="ac-profile-name">{profileForm.fullName}</h3>
              <p className="ac-profile-role">{profileForm.jobTitle}</p>
              <span className="ac-org-badge">{profileForm.organization}</span>
            </div>

            <div className="ac-card">
              <h3 className="ac-card-title" style={{ marginBottom: 14 }}>Account Details</h3>
              <div className="ac-detail-list">
                <div className="ac-detail-row">
                  <FiMail size={15} className="ac-detail-icon" />
                  <span className="ac-detail-text">{email || '—'}</span>
                </div>
                <div className="ac-detail-row">
                  <FiCalendar size={15} className="ac-detail-icon" />
                  <span className="ac-detail-text">Member since {memberSince}</span>
                </div>
                <div className="ac-detail-row">
                  <FiShield size={15} className="ac-detail-icon" />
                  <span className="ac-detail-text">
                    {userData?.role === 'admin' ? 'Admin Account' : 'Therapist Account'}
                  </span>
                </div>
              </div>
            </div>

            <div className="ac-card">
              <h3 className="ac-card-title" style={{ marginBottom: 14 }}>Quick Actions</h3>
              <div className="ac-quick-actions">
                <button className="ac-action-btn" onClick={() => toast('Data export coming soon.')}>
                  <FiDownload size={15} /> Export My Data
                </button>
                <button className="ac-action-btn" onClick={() => toast('Password change coming soon. Use your email provider to reset.')}>
                  <FiKey size={15} /> Change Password
                </button>
                <button className="ac-action-btn danger" onClick={handleLogOut}>
                  <FiLogOut size={15} /> Log Out
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="st-layout">

          {/* ── Left column ── */}
          <div className="st-col">

            <Section icon={<FiGlobe size={16} />} title={t('settings.language')}>
              <SettingRow label={t('settings.language')} sublabel={t('settings.languageSub')} last>
                <PillGroup
                  value={language}
                  onChange={setLanguage}
                  options={[
                    { value: 'en', label: 'English' },
                    { value: 'es', label: 'Español' },
                  ]}
                />
              </SettingRow>
            </Section>

            <Section icon={<FiMonitor size={16} />} title={t('settings.appearance')}>
              <SettingRow label={t('settings.textSize')} sublabel={t('settings.textSizeSub')}>
                <PillGroup
                  value={fontSize}
                  onChange={setFontSize}
                  options={[
                    { value: 'small',  label: 'S' },
                    { value: 'medium', label: 'M' },
                    { value: 'large',  label: 'L' },
                  ]}
                />
              </SettingRow>
              <SettingRow label={t('settings.animations')} sublabel={t('settings.animationsSub')} last>
                <Toggle checked={animations} onChange={setAnimations} />
              </SettingRow>
            </Section>

            <Section icon={<FiVolume2 size={16} />} title={t('settings.audio')}>
              <SettingRow
                label={t('settings.soundEffects')}
                sublabel={t('settings.soundEffectsSub')}
              >
                <Toggle checked={soundEffects} onChange={setSoundEffects} />
              </SettingRow>
              <SettingRow
                label={t('settings.voicePrompts')}
                sublabel={t('settings.voicePromptsSub')}
                last
              >
                <div className="st-row-group">
                  <Toggle checked={voicePrompts} onChange={setVoicePrompts} />
                  {voicePrompts && !window.speechSynthesis && (
                    <span className="st-warn">{t('settings.notSupported')}</span>
                  )}
                </div>
              </SettingRow>
            </Section>

            <Section icon={<FiSliders size={16} />} title={t('settings.session')}>
              <SettingRow
                label={t('settings.autoSave')}
                sublabel={t('settings.autoSaveSub')}
                last
              >
                <Toggle checked={autoSave} onChange={setAutoSave} />
              </SettingRow>
            </Section>

          </div>

          {/* ── Right column ── */}
          <div className="st-col">

            <Section icon={<FiMessageSquare size={16} />} title={t('settings.reportIssue')}>
              <div className="st-feedback-wrap">
                <p className="st-feedback-desc">
                  {t('settings.reportIssueDesc')}
                </p>
                <FeedbackForm type="manual" compact />
              </div>
            </Section>

            <Section icon={<FiDatabase size={16} />} title={t('settings.data')}>
              <SettingRow label={t('settings.autoSaveOn')} sublabel={t('settings.autoSaveOnSub')} last>
                <span className="st-status-badge st-status-badge--ok"><FiCheck size={11} /> {t('settings.on')}</span>
              </SettingRow>
              <div className="st-data-actions">
                <button className="st-data-btn st-data-btn--danger" onClick={handleClearData}>
                  <FiTrash2 size={13} />
                  {cleared ? t('settings.cleared') : t('settings.clearLocalData')}
                </button>
              </div>
            </Section>

            <Section icon={<FiInfo size={16} />} title={t('settings.about')}>
              <div className="st-about-list">
                {[
                  [t('settings.app'),      'Stimuli Central'],
                  [t('settings.version'),  '1.0.0'],
                  [t('settings.updated'),  'June 2026'],
                  [t('settings.support'),  'hello@stimulicentral.org'],
                ].map(([label, val]) => (
                  <div key={label} className="st-about-row">
                    <span className="st-about-label">{label}</span>
                    <span className="st-about-val">{val}</span>
                  </div>
                ))}
              </div>
            </Section>

            {isAdmin && (
              <Section icon={<FiActivity size={16} />} title={t('settings.auditLog')}>
                <div className="st-audit-controls">
                  <button className="st-data-btn" onClick={loadAuditLogs} disabled={auditLoading}>
                    {auditLoading ? t('settings.loading') : auditVisible ? t('settings.refresh') : t('settings.loadAuditLog')}
                  </button>
                  {auditVisible && (
                    <button className="st-data-btn" onClick={() => setAuditVisible(false)}>{t('settings.hide')}</button>
                  )}
                </div>
                {auditVisible && (
                  <div className="st-audit-table-wrap">
                    <table className="st-audit-table">
                      <thead>
                        <tr><th>Event</th><th>User ID</th><th>Details</th><th>Time</th></tr>
                      </thead>
                      <tbody>
                        {auditLogs.length === 0 ? (
                          <tr><td colSpan={4} className="st-audit-empty">No audit logs found.</td></tr>
                        ) : auditLogs.map(log => (
                          <tr key={log.id}>
                            <td className="st-audit-event">{log.event}</td>
                            <td className="st-audit-uid">{log.userId?.slice(0, 10)}…</td>
                            <td className="st-audit-meta">
                              {log.metadata ? Object.entries(log.metadata).map(([k, v]) => `${k}: ${v}`).join(', ') : '—'}
                            </td>
                            <td className="st-audit-time">{log.timestamp?.toDate?.()?.toLocaleString() || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            )}

          </div>
        </div>
      </div>
    </Layout>
  );
};
